import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout } from 'lucide-react';

const linkClass =
  'text-xs sm:text-sm font-medium text-gray-600 hover:text-green-700 whitespace-nowrap transition-colors';
const activeClass = 'text-green-800 font-semibold';

function readPublicUser() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Shared marketing navigation — not used inside designer dashboard.
 * Public nav includes yard photo feedback plus core marketing pages; plant library stays in designer flows.
 */
export default function PublicNav({
  beforeNav = null,
  currentUserId = null,
}) {
  const navigate = useNavigate();
  const { pathname: path } = useLocation();

  const user = readPublicUser();
  const isDesigner = user?.role === 'designer';
  const isCustomer = user?.role === 'customer';

  const isActive = (p) => path === p || (p !== '/' && path.startsWith(p));

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
      {beforeNav}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex flex-col gap-2 py-2 sm:py-0 sm:flex-row sm:justify-between sm:items-center sm:h-auto sm:min-h-[3.5rem]">
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/"
              className="flex items-center hover:opacity-80 transition-opacity py-1 -ml-1"
              aria-label="PlantaDay Home"
            >
              <img
                src="/plantadaylogo.png"
                alt="PlantaDay"
                className="h-9 sm:h-11 md:h-12 w-auto min-w-[48px]"
              />
            </Link>
            {isDesigner && (
              <div className="flex sm:hidden items-center gap-1">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="px-2 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg"
                >
                  Design
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:gap-x-3 md:justify-center flex-1 px-0 sm:px-2">
            <Link
              to="/"
              className={`${linkClass} ${path === '/' ? activeClass : ''}`}
              aria-current={path === '/' ? 'page' : undefined}
            >
              Home
            </Link>
            <Link
              to="/ideas"
              className={`${linkClass} ${isActive('/ideas') || isActive('/ask') || isActive('/ask-ai') ? activeClass : ''}`}
            >
              Get Ideas
            </Link>
            <Link
              to="/find-designer"
              className={`${linkClass} ${isActive('/find-designer') ? activeClass : ''}`}
            >
              Find a Designer
            </Link>
            <Link to="/learn" className={`${linkClass} ${isActive('/learn') ? activeClass : ''}`}>
              Learn
            </Link>
            <Link
              to="/community"
              className={`${linkClass} ${isActive('/community') ? activeClass : ''}`}
            >
              Community
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-2 flex-shrink-0 justify-end w-full sm:w-auto pt-1 sm:pt-0 border-t border-gray-100 sm:border-0">
            {isDesigner && (
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-3 py-2 text-xs sm:text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors shadow-sm flex items-center gap-1.5"
                title="Designer Dashboard"
              >
                <Layout className="w-4 h-4 hidden md:block" />
                <span className="hidden md:inline">Design &amp; collaborate</span>
                <span className="md:hidden">Design</span>
              </button>
            )}
            {currentUserId && isCustomer ? (
              <div className="flex items-center gap-1 flex-wrap justify-end">
                <Link
                  to="/dashboard/my-questions"
                  className={`px-2 py-2 text-xs sm:text-sm font-medium rounded-lg ${isActive('/dashboard/my-questions') ? activeClass : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  My questions
                </Link>
                <Link
                  to="/yard-profile"
                  className={`px-2 py-2 text-xs sm:text-sm font-medium rounded-lg ${path === '/yard-profile' ? activeClass : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  Yard profile
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user');
                    localStorage.removeItem('userData');
                    window.location.href = '/';
                  }}
                  className="px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  Log out
                </button>
              </div>
            ) : currentUserId ? (
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('user');
                  localStorage.removeItem('userData');
                  window.location.href = '/';
                }}
                className="px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Log out
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  Sign in
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/create-account')}
                  className="px-3 py-2 text-xs sm:text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Create account
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
